import { format, addDays as addDaysDateFns, differenceInDays, parseISO } from "date-fns";
import type { Locale } from "date-fns";

export interface DateValue {
  year: number;
  month: number;
  day: number;
  toString(): string;
}

// Generate array of dates between startDate and endDate
export function generateDateRange(startDate: DateValue, endDate: DateValue): DateValue[] {
  const start = new Date(startDate.year, startDate.month - 1, startDate.day);
  const end = new Date(endDate.year, endDate.month - 1, endDate.day);
  const dates: DateValue[] = [];

  let currentDate = start;
  while (currentDate <= end) {
    dates.push({
      year: currentDate.getFullYear(),
      month: currentDate.getMonth() + 1,
      day: currentDate.getDate(),
      toString() {
        return `${this.year}-${String(this.month).padStart(2, "0")}-${String(this.day).padStart(2, "0")}`;
      },
    });
    currentDate = addDaysDateFns(currentDate, 1);
  }

  return dates;
}

// Check if date range exceeds one month
export function isLongDateRange(startDate: DateValue, endDate: DateValue): boolean {
  const start = new Date(startDate.year, startDate.month - 1, startDate.day);
  const end = new Date(endDate.year, endDate.month - 1, endDate.day);
  return differenceInDays(end, start) > 30;
}

// Get day of week
export function getDayOfWeek(date: DateValue, t: (ns: any, key: string) => string): string {
  const jsDate = new Date(date.year, date.month - 1, date.day);
  const dayIndex = jsDate.getDay();
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return t("analytics", `date.${days[dayIndex]}`);
}

// Get labels for X axis (days or weeks depending on range)
export function getXAxisLabels(
  dateRange: DateValue[],
  isLongRange: boolean,
  t: (ns: any, key: string) => string,
  locale: Locale,
): string[] {
  if (!isLongRange) {
    // For short ranges show days
    return dateRange.map((date) => {
      const jsDate = new Date(date.year, date.month - 1, date.day);
      const day = jsDate.getDate();
      const dayOfWeek = format(jsDate, "EEE", { locale });
      const capitalizedDayOfWeek = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      return `${day} ${capitalizedDayOfWeek}`;
    });
  }

  // For long ranges show weeks
  const weekLabels: string[] = [];
  let i = 0;

  while (i < dateRange.length) {
    const startOfWeek = dateRange[i];
    const endIndex = Math.min(i + 6, dateRange.length - 1);
    const endOfWeek = dateRange[endIndex];

    const startDate = new Date(startOfWeek.year, startOfWeek.month - 1, startOfWeek.day);
    const endDate = new Date(endOfWeek.year, endOfWeek.month - 1, endOfWeek.day);

    const startFormatted = format(startDate, "d MMM", { locale });
    const endFormatted = format(endDate, "d MMM", { locale });

    weekLabels.push(`${startFormatted} - ${endFormatted}`);
    i += 7;
  }

  return weekLabels;
}

// Utilities for working with dates
export function getInitialDate(queryDate: string | undefined, type: "start" | "end"): DateValue {
  if (queryDate) {
    try {
      const date = parseISO(queryDate);
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        toString() {
          return `${this.year}-${String(this.month).padStart(2, "0")}-${String(this.day).padStart(2, "0")}`;
        },
      };
    } catch {
      // Fallback to default
    }
  }

  const now = new Date();
  if (type === "start") {
    // 7 days ago
    const startDate = addDaysDateFns(now, -7);
    return {
      year: startDate.getFullYear(),
      month: startDate.getMonth() + 1,
      day: startDate.getDate(),
      toString() {
        return `${this.year}-${String(this.month).padStart(2, "0")}-${String(this.day).padStart(2, "0")}`;
      },
    };
  } else {
    // Today
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      toString() {
        return `${this.year}-${String(this.month).padStart(2, "0")}-${String(this.day).padStart(2, "0")}`;
      },
    };
  }
}

/** "N ч M мин"-style duration from seconds, in the app's language. */
export function formatDurationShort(seconds: number, t: (ns: any, key: string) => string): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h} ${t("analytics", "units.hoursShort")} ${m} ${t("analytics", "units.minutesShort")}`;
}
